<script>
  import { push, link } from 'svelte-spa-router';
  import { login, logout, refreshSession, authStore } from '../stores/auth.js';
  import { API_ROOT, authApi } from '../stores/api.js';
  import { safeOAuthReturnTo } from '../lib/oauthReturnTo.js';
  import { oauthResumeTarget } from '../lib/oauthResume.js';
  import { navigateTo } from '../lib/navigation.js';
  import { _ } from '../lib/i18n.js';
  import { theme } from '../stores/theme.js';
  import { onMount } from 'svelte';
  import { Sun, Moon } from 'lucide-svelte';

  let isPreparing = false;
  let isResuming = false;
  let registrationOpen = false;

  // Modes that admit new accounts. Anything else — `closed`, an unknown or
  // differently spelled mode, an error — keeps "Crea un account" hidden.
  const SIGN_UP_MODES = ['open', 'invite'];

  async function loadRegistrationMode() {
    try {
      const { mode } = await authApi.getRegistration();
      registrationOpen = SIGN_UP_MODES.includes(mode);
    } catch {
      registrationOpen = false;
    }
  }

  onMount(async () => {
    theme.init();
    loadRegistrationMode();
    const params = new URLSearchParams(window.location.search);
    // reauth=1 wins over resume=1: a fresh password is what was asked for.
    if (params.get('reauth') === '1') {
      isPreparing = true;
      await logout();
      isPreparing = false;
    } else if (params.get('resume') === '1') {
      await resumeHandshake();
    }
  });

  /**
   * The login handler found an expired access token: renew the session and go
   * back, once per challenge. A second attempt, or a refresh the server
   * refuses, goes back with `resume_failed=1`, which the handler treats as
   * signed out — so this never loops.
   */
  async function resumeHandshake() {
    const target = oauthResumeTarget(window.location.search, API_ROOT, window.sessionStorage);
    if (!target) return;
    isResuming = true;
    if (target.alreadyTried) {
      navigateTo(target.failedUrl);
      return;
    }
    const refreshed = await refreshSession();
    navigateTo(refreshed ? target.url : target.failedUrl);
  }

  let email = '';
  let password = '';
  let error = '';
  let isLoading = false;

  async function handleLogin(event) {
    event.preventDefault();

    if (isPreparing) return;

    if (!email || !password) {
      error = $_('auth.requiredFields');
      return;
    }

    isLoading = true;
    error = '';

    const result = await login(email, password);

    if (result.success) {
      // An OAuth handshake that found nobody signed in parked its continuation
      // in `?returnTo=`. It is followed only when it points at our own
      // authorization server: this form is a public page, and a login that
      // forwards anywhere is an open redirect wearing our branding.
      const returnTo = safeOAuthReturnTo(window.location.search, API_ROOT);
      if (returnTo) {
        window.location.assign(returnTo);
        return;
      }
      push('/');
    } else {
      error = result.error;
      isLoading = false;
    }
  }
</script>

<div class="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 retro-grid transition-colors duration-300" style="background-color: var(--color-bg-dark);">
  <!-- Theme Toggle -->
  <button
    on:click={() => theme.toggle()}
    class="absolute top-4 right-4 h-10 w-10 inline-flex items-center justify-center text-gray-400 hover:text-primary-500 transition-all duration-100 border-2 border-transparent hover:border-primary-500 z-20"
    title={$theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
  >
    {#if $theme === 'dark'}
      <Sun size={18} />
    {:else}
      <Moon size={18} />
    {/if}
  </button>

  <!-- Background decorations - retro glow orbs -->
  <div class="absolute inset-0 overflow-hidden pointer-events-none">
    <div class="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl" style="background: rgba(255, 0, 255, 0.08);"></div>
    <div class="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl" style="background: rgba(0, 240, 255, 0.08);"></div>
  </div>

  <div class="relative z-10 sm:mx-auto sm:w-full sm:max-w-md fade-in">
    <!-- Logo section -->
    <div class="flex justify-center mb-8">
      <div class="relative scale-in">
        <div class="absolute inset-0 blur-2xl rounded-full" style="background: rgba(0, 240, 255, 0.2);"></div>
        <img class="relative h-20 w-auto drop-shadow-lg" src="/webdeploy-icon.png" alt="WEBDEPLOY" />
      </div>
    </div>

    <!-- Title -->
    <div class="text-center slide-up">
      <h1 class="text-5xl font-bold text-gradient font-display tracking-widest">{$_('system.appName')}</h1>
      <h2 class="mt-3 text-lg font-bold uppercase tracking-widest" style="color: var(--color-text-heading);">
        {$_('system.appSubtitle')}
      </h2>
      <p class="mt-2 text-sm" style="color: var(--color-text-muted);">
        {$_('auth.login')} {$_('auth.loginToSystem')}
      </p>
    </div>
  </div>

  <div class="relative z-10 mt-12 sm:mx-auto sm:w-full sm:max-w-md slide-up">
    <div class="card-elevated p-8">
      {#if isResuming}
        <div class="text-center py-6" data-testid="login-resuming">
          <svg class="loading-spinner w-8 h-8 mx-auto rounded-full" />
          <p class="mt-4 text-sm" style="color: var(--color-text-muted);">{$_('auth.resuming')}</p>
        </div>
      {:else}
      <form on:submit={handleLogin} class="space-y-6">
        {#if error}
          <div class="border-2 border-red-500 p-4 scale-in" style="background-color: rgba(239, 68, 68, 0.1);">
            <div class="flex">
              <div class="flex-shrink-0">
                <div class="w-5 h-5 text-red-500 flex items-center justify-center font-bold">!</div>
              </div>
              <div class="ml-3">
                <p class="text-sm font-bold text-red-400">{error}</p>
              </div>
            </div>
          </div>
        {/if}

        <div class="space-y-5">
          <div>
            <label for="email" class="label flex items-center space-x-2">
              <svg class="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
              </svg>
              <span>{$_('auth.email')}</span>
            </label>
            <input
              id="email"
              type="email"
              bind:value={email}
              required
              autocomplete="email"
              placeholder={$_('auth.emailPlaceholder')}
              class="input {isLoading ? 'opacity-50 cursor-not-allowed' : ''}"
              disabled={isLoading || isPreparing}
            />
          </div>

          <div>
            <label for="password" class="label flex items-center space-x-2">
              <svg class="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              <span>{$_('auth.password')}</span>
            </label>
            <input
              id="password"
              type="password"
              bind:value={password}
              required
              autocomplete="current-password"
              placeholder={$_('auth.passwordPlaceholder')}
              class="input {isLoading ? 'opacity-50 cursor-not-allowed' : ''}"
              disabled={isLoading || isPreparing}
            />
            <div class="mt-2 flex justify-end">
              <a
                href="/forgot-password"
                use:link
                class="text-sm font-bold text-primary-500 hover:underline"
                data-testid="forgot-password-link"
              >
                {$_('auth.forgotPassword')}
              </a>
            </div>
          </div>
        </div>

        <div class="pt-4">
          <button
            type="submit"
            class="w-full btn btn-primary h-12 text-base"
            disabled={isLoading || isPreparing}
          >
            {#if isLoading}
              <svg class="loading-spinner w-5 h-5 mr-3 rounded-full" />
              <span>{$_('common.loading')}...</span>
            {:else}
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              <span>{$_('auth.login')}</span>
            {/if}
          </button>
        </div>
      </form>
      {/if}

      {#if registrationOpen}
        <p class="mt-6 text-center text-sm">
          <a
            href="/register"
            use:link
            class="font-bold text-primary-500 hover:underline"
            data-testid="create-account-link"
          >
            {$_('auth.createAccount')}
          </a>
        </p>
      {/if}

      <!-- Footer info -->
      <div class="mt-8 pt-6 border-t-2" style="border-color: var(--color-border-default);">
        <div class="text-center space-y-2">
          <p class="text-sm" style="color: var(--color-text-muted);">
            {$_('system.companyDescription')}
          </p>
          <p class="text-xs" style="color: var(--color-text-muted);">
            {$_('system.version')}
          </p>
        </div>
      </div>
    </div>
  </div>
</div>
